import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withStyles, makeStyles, createStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Pagination from "@material-ui/lab/Pagination";
import {
  setTotalFiles,
  setAreaGroup,
  setCurrentPageNumber,
} from "../../actions";

const useStyles = makeStyles((theme) =>
  createStyles({
    tableWrapper: {
      width: "100%",
    },
    tableWrapperSlim: {
      width: "89%",
    },
    table: {
      width: "100%",
    },
    tableHead: {
      height: 40,
      backgroundColor: "#fafafa",
    },
    tableRow: {
      height: 126,
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    },
    tableRowNone: {
      height: 126,
    },
    noDataCell: {
      textAlign: "center",
      padding: theme.spacing(3),
      fontSize: 14,
      color: theme.palette.text.secondary,
    },
    pagination: {
      display: "flex",
      justifyContent: "center",
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(4),
    },
  })
);

const DownloadTable = ({
  setCurrentPageNumber,
  open,
  currentPageNumber,
  companyName,
  setTotalFiles,
  setAreaGroup,
  filterDownload,
}) => {
  const classes = useStyles();
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Redux に保存しているページ番号 → ローカル state に反映
  useEffect(() => {
    setCurrentPage(currentPageNumber || 1);
  }, [currentPageNumber]);

  // download.json を読み込む（初回のみ）
  useEffect(() => {
    const fetchDownloadList = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_PICTREE_DATA_BASE_URL}/tepco/download/download.json`
        );
        if (!res.ok) {
          throw new Error(`Failed to load download.json: ${res.status}`);
        }
        const data = await res.json();

        setTableData(data);

        // 全件数
        setTotalFiles(data.length);

        // 県域ごとの件数
        const areaCountMap = data.reduce((acc, item) => {
          if (!item.area) return acc;
          acc[item.area] = (acc[item.area] || 0) + 1;
          return acc;
        }, {});

        const areaOptions = [
          {
            value: "all",
            label: "全て",
            count: data.length,
          },
          ...Object.entries(areaCountMap).map(([area, count]) => ({
            value: area,
            label: `${area}（${count}件）`,
            count,
          })),
        ];

        setAreaGroup(areaOptions);
      } catch (err) {
        console.error("Error loading download list:", err);
        setTableData([]);
        setTotalFiles(0);
        setAreaGroup([
          {
            value: "all",
            label: "全て",
            count: 0,
          },
        ]);
      }
    };

    fetchDownloadList();
  }, [setTotalFiles, setAreaGroup]);

  const handleDownload = (url) => {
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = url; // ここに CloudFront URL
    document.body.appendChild(iframe);

    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 60000);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    setCurrentPageNumber(value);
  };

  // ---- フィルター適用ロジック ----
  const selectedAreas =
    (filterDownload &&
      filterDownload.areaGroup &&
      filterDownload.areaGroup.value) ||
    [];

  const dateRange = filterDownload && filterDownload.dateRange;
  const hasDateRange =
    dateRange && dateRange.start && dateRange.end;

  // 1) 県域フィルタ
  const areaFiltered =
    selectedAreas.length === 0 || selectedAreas.includes("all")
      ? tableData
      : tableData.filter((item) => selectedAreas.includes(item.area));

  // 2) 日付レンジフィルタ（文字列 YYYY-MM-DD 同士なので比較OK）
  const filteredData = hasDateRange
    ? areaFiltered.filter((item) => {
        if (!item.date) return false;
        return item.date >= dateRange.start && item.date <= dateRange.end;
      })
    : areaFiltered;

  // ページングは「絞り込まれた件数」を基準にする
  const totalFiltered = filteredData.length;
  const pageCount = Math.ceil(totalFiltered / itemsPerPage) || 1;

  // 現在ページに表示するデータだけを取り出す
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  return (
    <>
      <Grid
        container
        direction="column"
        justifyContent={"center"}
        className={open ? classes.tableWrapperSlim : classes.tableWrapper}
      >
        <Grid item>
          <TableContainer component={Paper}>
            <Table stickyHeader className={classes.table}>
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell align="center">#</TableCell>
                  <TableCell align="center">ファイル名</TableCell>
                  <TableCell align="center">作成日時</TableCell>
                  <TableCell align="center">ダウンロード</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length > 0 &&
                  paginatedData.map((row, index) => (
                    <TableRow
                      className={classes.tableRow}
                      key={row.id || index}
                    >
                      <TableCell
                        component="th"
                        scope="row"
                        style={{ width: "10%" }}
                        align="center"
                      >
                        {startIndex + index + 1}
                      </TableCell>

                      <TableCell style={{ width: "30%" }} align="center">
                        {row.fileName}
                      </TableCell>

                      <TableCell style={{ width: "20%" }} align="center">
                        {row.date}
                      </TableCell>
                      <TableCell style={{ width: "20%" }} align="center">
                        <button
                          type="button"
                          onClick={() => handleDownload(row.url)}
                          style={{
                            cursor: "pointer",
                            color: "blue",
                            textDecoration: "underline",
                            background: "none",
                            border: "none",
                            padding: 0,
                          }}
                        >
                          ダウンロード
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}

                {paginatedData.length === 0 && (
                  <TableRow className={classes.tableRowNone}>
                    <TableCell colSpan={4} className={classes.noDataCell}>
                      データがありません
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* 件数が 0 のときはページネーション非表示 */}
        {totalFiltered > 0 && (
          <Grid item>
            <div className={classes.pagination}>
              <Pagination
                count={pageCount}
                page={currentPage}
                onChange={handlePageChange}
                color="secondary"
              />
            </div>
          </Grid>
        )}
      </Grid>
    </>
  );
};

DownloadTable.propTypes = {
  currentPageNumber: PropTypes.number,
  setCurrentPageNumber: PropTypes.func.isRequired,
  open: PropTypes.bool,
  companyName: PropTypes.string,
  setTotalFiles: PropTypes.func.isRequired,
  setAreaGroup: PropTypes.func.isRequired,
  filterDownload: PropTypes.object,
};

const mapStateToProps = (state) => ({
  currentPageNumber: state.home.currentPageNumber,
  filterDownload: state.download.filterDownload,
});

const mapDispatchToProps = (dispatch) => ({
  setTotalFiles: (param) => dispatch(setTotalFiles(param)),
  setAreaGroup: (param) => dispatch(setAreaGroup(param)),
  setCurrentPageNumber: (param) => dispatch(setCurrentPageNumber(param)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(null, { withTheme: true })(DownloadTable));
